import type { CandidateComp } from "./types";
import { isCompetingListing } from "./constants";

/**
 * Days on market, as it stands TODAY — for the worksheet the agent selects comps on.
 *
 * WHAT WAS WRONG (2026-08-04, owner). Three things at once:
 *
 *  1. The figure was in the report and NOWHERE ON THE SELECTION SCREEN, which is the screen where
 *     an agent decides which sales to price from and which competition to show a seller.
 *  2. 3611 Cedar Creek Lane came through blank because its record prints `CDOM: 71` and no DOM at
 *     all. Reporting nothing there tells the agent less than the sheet does.
 *  3. An MLS export is a SNAPSHOT. A home showing 57 days when the file was pulled has been
 *     sitting for 57 plus however long ago that was, and quoting the stale figure to a seller
 *     misstates the competition's exposure by exactly the age of the file.
 *
 * ONLY A HOME STILL ON THE MARKET ACCRUES. A closed sale's days on market is fixed at closing —
 * 4507 Oakgrove closed after 73 days and it is 73 days forever. Adding elapsed time to it would
 * invent a number describing nothing. That distinction is the whole of the correctness here, and
 * it mirrors `engine.amb.current_days_on_market`, which is authoritative for the deliverable.
 *
 * The label is always the words "Days on market". Never "DOM" — the owner asked for it spelled
 * out, and an abbreviation that means something precise to an MLS is jargon to a seller.
 */

/** The figure the sheet is stating: DOM when printed, CDOM when only that is. */
export function statedDaysOnMarket(c: CandidateComp): number | null {
  for (const v of [c.dom, c.cdom]) {
    if (typeof v === "number" && Number.isFinite(v) && v >= 0) return v;
  }
  return null;
}

/** CDOM materially above DOM means the home was listed, withdrawn and listed again — real signal
 * about the competition, so it is surfaced rather than collapsed into one number. */
export function isRelisted(c: CandidateComp): boolean {
  return (
    typeof c.dom === "number" && typeof c.cdom === "number" && c.cdom > c.dom
  );
}

function parseISO(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `asOf` is the date the figure was true — when the file was read. `todayISO` is injectable so
 * this is testable without freezing the clock. */
export function currentDaysOnMarket(
  c: CandidateComp,
  asOf?: string | null,
  todayISO?: string
): number | null {
  const base = statedDaysOnMarket(c);
  if (base === null) return null;
  if (!isCompetingListing(c)) return base;          // a closed sale stopped accruing
  const a = parseISO(asOf);
  const t = parseISO(todayISO ?? new Date().toISOString().slice(0, 10));
  if (!a || !t) return base;                        // no date to reason from -> the sheet's own
  const elapsed = Math.floor((t.getTime() - a.getTime()) / 86_400_000);
  return elapsed > 0 ? base + elapsed : base;
}

/** What the cell shows, including the honest reason a figure is absent. */
export function daysOnMarketLabel(
  c: CandidateComp,
  asOf?: string | null,
  todayISO?: string
): string {
  const n = currentDaysOnMarket(c, asOf, todayISO);
  if (n === null) return "—";
  const stated = statedDaysOnMarket(c);
  const grew = stated !== null && n > stated;
  return grew ? `${n} (${stated} when read)` : `${n}`;
}

/** The cumulative figure, as its own stated fact.
 *
 * Owner, 2026-08-04: "we can also share the Cumulative Days on Market (CDOM) in the comp
 * selection area." Shown whenever the sheet prints one — not only when it exceeds DOM — because
 * "cumulative equals current" is itself information: it says the home has been listed once and
 * has not been relisted. Withholding it until the numbers disagree makes its absence ambiguous.
 *
 * Spelled out, like everything else on this worksheet. The owner's rule was "never say DOM"; an
 * abbreviation that is precise inside an MLS is jargon at a kitchen table, and CDOM doubly so.
 * NEVER brought forward to today: cumulative days belong to the listing history the sheet is
 * reporting, and adding elapsed time to a history would be inventing one. */
export function cumulativeDaysOnMarketLabel(c: CandidateComp): string | null {
  if (typeof c.cdom !== "number" || !Number.isFinite(c.cdom) || c.cdom < 0) return null;
  return isRelisted(c)
    ? `Cumulative ${c.cdom} — relisted`
    : `Cumulative ${c.cdom}`;
}
